import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionButton from '../../components/ui/QuickActionButton';
import DealershipTable from './components/DealershipTable';
import DealershipFilters from './components/DealershipFilters';
import DealershipModal from './components/DealershipModal';
import ExportModal from './components/ExportModal';
import BulkActions from './components/BulkActions';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import { carHouseService } from '../../services/carHouseService';

const DealershipManagement = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    search: ''
  });
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  
  // Modal states
  const [dealershipModal, setDealershipModal] = useState({
    isOpen: false,
    mode: 'view', // 'view', 'edit', 'create'
    dealership: null
  });
  const [exportModal, setExportModal] = useState({ isOpen: false });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    dealerships: null,
    isBulk: false
  });

  const [dealerships, setDealerships] = useState([]);
  const [filteredDealerships, setFilteredDealerships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load dealerships from database
  useEffect(() => {
    const loadDealerships = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const { data, error } = await carHouseService.getAllCarHouses(filters);
        
        if (error) {
          throw error;
        }

        // Transform database data to frontend format
        const transformedData = data?.map(carHouse => carHouseService.transformToFrontendFormat(carHouse)) || [];
        setDealerships(transformedData);
      } catch (err) {
        console.error('Error loading dealerships:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadDealerships();
  }, [filters]);

  // Set filtered dealerships to match dealerships since filtering is done server-side
  useEffect(() => {
    setFilteredDealerships(dealerships);
  }, [dealerships]);

  // Selection handlers
  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedItems(prev => [...prev, id]);
    } else {
      setSelectedItems(prev => prev?.filter(item => item !== id));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(filteredDealerships?.map(d => d?.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  // Filter handlers
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: ''
    });
  };

  // Refresh data from database
  const refreshData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await carHouseService.getAllCarHouses(filters);
      
      if (error) {
        throw error;
      }

      // Transform database data to frontend format
      const transformedData = data?.map(carHouse => carHouseService.transformToFrontendFormat(carHouse)) || [];
      setDealerships(transformedData);
    } catch (err) {
      console.error('Error refreshing dealerships:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Modal handlers
  const handleCreateDealership = () => {
    setDealershipModal({
      isOpen: true,
      mode: 'create',
      dealership: null
    });
  };

  const handleViewDealership = (dealership) => {
    setDealershipModal({
      isOpen: true,
      mode: 'view',
      dealership
    });
  };

  const handleEditDealership = (dealership) => {
    setDealershipModal({
      isOpen: true,
      mode: 'edit',
      dealership
    });
  };

  const handleDeleteDealership = (dealership) => {
    setDeleteModal({
      isOpen: true,
      dealerships: dealership,
      isBulk: false
    });
  };

  const handleBulkEdit = () => {
    // In a real application, this would open a bulk edit modal
  };

  const handleBulkDelete = () => {
    const selectedDealerships = dealerships?.filter(d => selectedItems?.includes(d?.id));
    setDeleteModal({
      isOpen: true,
      dealerships: selectedDealerships,
      isBulk: true
    });
  };

  const handleExport = () => {
    setExportModal({ isOpen: true });
  };

  // Save handlers
  const handleSaveDealership = async (formData) => {
    try {
      if (dealershipModal?.mode === 'create') {
        const { data, error } = await carHouseService.createCarHouse(formData);
        
        if (error) {
          throw error;
        }

        // Transform the new car house to frontend format and add to state
        const newDealership = carHouseService.transformToFrontendFormat(data);
        setDealerships(prev => [...prev, newDealership]);
      } else if (dealershipModal?.mode === 'edit') {
        const { data, error } = await carHouseService.updateCarHouse(dealershipModal?.dealership?.id, formData);
        
        if (error) {
          throw error;
        }

        // Transform the updated car house to frontend format and update state
        const updatedDealership = carHouseService.transformToFrontendFormat(data);
        setDealerships(prev =>
          prev?.map(d =>
            d?.id === dealershipModal?.dealership?.id
              ? updatedDealership
              : d
          )
        );
      }
      
      setDealershipModal({ isOpen: false, mode: 'view', dealership: null });
    } catch (err) {
      console.error('Error saving dealership:', err);
      // You might want to show an error message to the user here
      throw err;
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteModal?.isBulk) {
        const idsToDelete = deleteModal?.dealerships?.map(d => d?.id);
        const { error } = await carHouseService.bulkDeleteCarHouses(idsToDelete);
        
        if (error) {
          throw error;
        }

        setDealerships(prev => prev?.filter(d => !idsToDelete?.includes(d?.id)));
        setSelectedItems([]);
      } else {
        const { error } = await carHouseService.deleteCarHouse(deleteModal?.dealerships?.id);
        
        if (error) {
          throw error;
        }

        setDealerships(prev => prev?.filter(d => d?.id !== deleteModal?.dealerships?.id));
      }
      
      setDeleteModal({ isOpen: false, dealerships: null, isBulk: false });
    } catch (err) {
      console.error('Error deleting dealership(s):', err);
      // You might want to show an error message to the user here
      throw err;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header userRole="admin" />
      {/* Hide sidebar on mobile */}
      <div className="hidden lg:block">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userRole="admin"
        />
      </div>
      {/* Main Content */}
      <main className={`pt-16 transition-all duration-200 ${
        isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
      }`}>
        <div className="p-4 lg:p-6">
          {/* Breadcrumb */}
          <Breadcrumb />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Autohaus-Verwaltung
              </h1>
              <p className="text-muted-foreground">
                Verwalten Sie Ihre Partner-Autohäuser und deren Kontaktdaten
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {/* <Button
                variant="outline"
                onClick={handleExport}
                disabled={filteredDealerships?.length === 0 || isLoading}
              >
                <Icon name="Download" size={16} className="mr-2" />
                Exportieren
              </Button> */}
              <Button
                variant="default"
                onClick={handleCreateDealership}
                disabled={isLoading}
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Neues Autohaus
              </Button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-error font-medium">Fehler beim Laden der Autohäuser</p>
              </div>
              <p className="text-error/80 text-sm mt-1">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
                className="mt-2"
              >
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Erneut versuchen
              </Button>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Gesamt</p>
                  <p className="text-2xl font-bold text-foreground">{dealerships?.length}</p>
                </div>
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Building2" size={20} className="text-primary" />
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Aufträge</p>
                  <p className="text-2xl font-bold text-accent">
                    {dealerships?.reduce((sum, d) => sum + d?.activeOrders, 0)}
                  </p>
                </div>
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="ClipboardList" size={20} className="text-accent" />
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Städte</p>
                  <p className="text-2xl font-bold text-secondary">
                    {new Set(dealerships.map(d => d.city))?.size}
                  </p>
                </div>
                <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                  <Icon name="MapPin" size={20} className="text-secondary" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <DealershipFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              isExpanded={isFiltersExpanded}
              onToggleExpanded={() => setIsFiltersExpanded(!isFiltersExpanded)}
            />
          </div>

          {/* Results Info */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              {filteredDealerships?.length === dealerships?.length ? (
                `${dealerships?.length} Autohäuser`
              ) : (
                `${filteredDealerships?.length} von ${dealerships?.length} Autohäusern`
              )}
              {selectedItems?.length > 0 && (
                <span className="ml-2 text-primary font-medium">
                  • {selectedItems?.length} ausgewählt
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                disabled={!filters?.search}
              >
                <Icon name="Search" size={16} className="mr-2" />
                Suche zurücksetzen
              </Button>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Loader2" size={32} className="text-muted-foreground animate-spin" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                Autohäuser werden geladen...
              </h3>
              <p className="text-muted-foreground">
                Bitte warten Sie einen Moment.
              </p>
            </div>
          )}

          {/* Table */}
          {!isLoading && (
            <>
              <DealershipTable
                dealerships={filteredDealerships}
                onEdit={handleEditDealership}
                onView={handleViewDealership}
                onDelete={handleDeleteDealership}
                selectedItems={selectedItems}
                onSelectItem={handleSelectItem}
                onSelectAll={handleSelectAll}
              />

              {/* Empty State */}
              {filteredDealerships?.length === 0 && (
                <div className="bg-card rounded-lg border border-border p-12 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Building2" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Keine Autohäuser gefunden
                  </h3>
                                     <p className="text-muted-foreground mb-6">
                     {filters?.search
                       ? 'Versuchen Sie, Ihre Filter anzupassen oder zu entfernen.' :'Erstellen Sie Ihr erstes Autohaus, um zu beginnen.'
                     }
                   </p>
                   {!filters?.search && (
                    <Button
                      variant="default"
                      onClick={handleCreateDealership}
                    >
                      <Icon name="Plus" size={16} className="mr-2" />
                      Neues Autohaus erstellen
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      {/* Bulk Actions */}
      <BulkActions
        selectedItems={selectedItems}
        onClearSelection={handleClearSelection}
        onBulkEdit={handleBulkEdit}
        onBulkDelete={handleBulkDelete}
        onExport={handleExport}
      />
      {/* Quick Action Button */}
      <QuickActionButton userRole="admin" />
      {/* Modals */}
      <DealershipModal
        isOpen={dealershipModal?.isOpen}
        onClose={() => setDealershipModal({ isOpen: false, mode: 'view', dealership: null })}
        dealership={dealershipModal?.dealership}
        onSave={handleSaveDealership}
        mode={dealershipModal?.mode}
      />
      <ExportModal
        isOpen={exportModal?.isOpen}
        onClose={() => setExportModal({ isOpen: false })}
        selectedItems={selectedItems}
        totalItems={filteredDealerships?.length}
        allDealerships={filteredDealerships}
      />
      <DeleteConfirmModal
        isOpen={deleteModal?.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, dealerships: null, isBulk: false })}
        onConfirm={handleConfirmDelete}
        dealerships={deleteModal?.dealerships}
        isBulk={deleteModal?.isBulk}
      />
    </div>
  );
};

export default DealershipManagement;